import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useRealtime } from "@/lib/realtime";
import MapView from "@/components/MapView";
import MobileBrandHeader from "@/components/MobileBrandHeader";
import { toast } from "sonner";
import {
  Search, MapPin, Building2, Home as HomeIcon, Briefcase, Sparkles, ArrowRight,
  Star, User, Settings, LogOut, ChevronDown, Leaf
} from "lucide-react";

const ENEC_PLACES = [
  { name: "ENEC HQ", name_ar: "المقر", lat: 24.4539, lng: 54.3773, icon: Building2 },
  { name: "Barakah NPP", name_ar: "محطة براكة", lat: 23.9686, lng: 52.1925, icon: Sparkles },
  { name: "Al Raha", name_ar: "الراحة", lat: 24.4275, lng: 54.6097, icon: Briefcase },
];

export default function Home() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [active, setActive] = useState(null);
  const [places, setPlaces] = useState([]);
  const [frequent, setFrequent] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const userLoc = user?.home_lat ? [user.home_lat, user.home_lng] : [24.4539, 54.3773];

  const reload = () => {
    api.get(`/vehicles/nearby?lat=${userLoc[0]}&lng=${userLoc[1]}`).then((r) => setVehicles(r.data || [])).catch(() => {});
    api.get("/bookings/active").then((r) => setActive(r.data)).catch(() => {});
  };

  useEffect(() => {
    reload();
    api.get("/me/saved-places").then((r) => setPlaces(r.data || [])).catch(() => {});
    api.get("/me/frequent-routes").then((r) => setFrequent(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoc[0], userLoc[1]]);

  useRealtime((msg) => {
    if (msg.type === "notification") {
      toast.info(msg.data?.title || "TESLAM", { description: msg.data?.body });
      reload();
    } else if (msg.type === "driver_location") {
      reload();
    }
  });

  const hour = new Date().getHours();
  const greet = hour < 12 ? t("good_morning") : hour < 18 ? t("good_afternoon") : t("good_evening");
  const home = places.find((p) => p.is_home);
  const favorites = places.filter((p) => p.is_favorite && !p.is_home);

  return (
    <div className="relative h-screen overflow-hidden">
      <div className="absolute inset-0">
        <MapView userLocation={userLoc} vehicles={vehicles} center={userLoc} zoom={12} />
      </div>

      {/* Brand strip */}
      <div className="absolute top-0 left-0 right-0 z-40">
        <MobileBrandHeader testId="employee-brand-header" />
      </div>

      {/* Top glass card with user dropdown */}
      <div className="absolute top-[64px] left-0 right-0 px-4 z-30 fade-up">
        <div
          className="rounded-2xl px-4 py-3 flex items-center justify-between"
          style={{
            background: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(28px) saturate(180%)",
            WebkitBackdropFilter: "blur(28px) saturate(180%)",
            border: "1px solid rgba(255,255,255,0.6)",
            boxShadow: "0 12px 36px rgba(15,23,42,0.10)",
          }}
        >
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{greet}</p>
            <p className="font-display font-bold text-slate-900 text-lg leading-tight truncate" data-testid="home-greeting">
              {user?.name?.split(" ")[0]}
            </p>
          </div>

          <button
            onClick={() => setShowMenu((s) => !s)}
            data-testid="user-menu-toggle"
            className="flex items-center gap-2 pl-2 pr-3 h-11 rounded-full bg-white border border-slate-200 hover:border-primary transition active:scale-95"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-bold text-sm uppercase">
              {user?.name?.charAt(0)}
            </div>
            <ChevronDown size={14} className={`text-slate-500 transition-transform ${showMenu ? "rotate-180" : ""}`} />
          </button>
        </div>

        {showMenu && (
          <div
            className="mt-2 rounded-2xl overflow-hidden fade-up"
            data-testid="user-menu"
            style={{
              background: "rgba(255,255,255,0.96)",
              backdropFilter: "blur(28px) saturate(180%)",
              WebkitBackdropFilter: "blur(28px) saturate(180%)",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "0 18px 48px rgba(15,23,42,0.18)",
            }}
          >
            <div className="px-4 py-4 border-b border-slate-200/70 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-display font-bold text-xl">
                {user?.name?.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-slate-900 truncate">{user?.name}</p>
                <p className="text-[10px] font-mono text-slate-500">{user?.enec_id} · {user?.department}</p>
              </div>
            </div>
            <div className="p-1">
              <MenuItem to="/app/profile" icon={User} label={`${t("profile")} & ${t("saved_places")}`} testId="menu-profile" />
              <MenuItem to="/app/profile" icon={Leaf} label={t("my_co2")} testId="menu-sustain" />
              <MenuItem to="/app/profile" icon={Settings} label={t("preferences")} testId="menu-prefs" />
              <button
                onClick={() => { logout(); nav("/login", { replace: true }); }}
                data-testid="menu-logout"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 text-rose-600 text-sm font-medium transition active:scale-[0.98]"
              >
                <LogOut size={16} /> {t("sign_out")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active ride banner */}
      {active && (
        <div
          onClick={() => nav(`/app/track/${active.id}`)}
          className="absolute top-[130px] left-4 right-4 bg-slate-900 text-white rounded-2xl px-4 py-3 shadow-2xl z-20 flex items-center justify-between gap-2 cursor-pointer fade-up active:scale-[0.99] transition"
          data-testid="active-ride-banner"
        >
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wider text-primary font-bold">{t("active_ride")}</p>
            <p className="font-semibold text-sm truncate">{active.dropoff_address}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-[10px] uppercase font-bold tracking-wide whitespace-nowrap">
              {active.status.replace(/_/g, " ")}
            </span>
            <ArrowRight size={18} />
          </div>
        </div>
      )}

      {/* Bottom sheet */}
      <div className="absolute bottom-24 left-0 right-0 px-4 z-10 sheet-enter">
        <div
          className="rounded-3xl p-5"
          style={{
            background: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.7)",
            boxShadow: "0 24px 60px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.04)",
          }}
        >
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-1">{t("where_to")}</h2>
          <p className="text-sm text-slate-500 mb-4">{t("complimentary_sub")}</p>

          <button
            data-testid="home-where-to"
            onClick={() => nav("/app/book")}
            className="w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-4 py-4 flex items-center gap-3 transition-all active:scale-[0.98]"
          >
            <Search size={20} className="text-primary" />
            <span className="text-slate-500 font-medium">{t("search_destination")}</span>
          </button>

          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">{t("quick_destinations")}</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {home && (
                <QuickChip
                  testId={`quick-home`}
                  icon={HomeIcon}
                  label="Home"
                  starred
                  onClick={() => nav("/app/book", { state: { dropoff: { lat: home.lat, lng: home.lng, address: home.address } } })}
                />
              )}
              {ENEC_PLACES.map((p) => (
                <QuickChip
                  key={p.name}
                  testId={`quick-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  icon={p.icon}
                  label={p.name}
                  onClick={() => nav("/app/book", { state: { dropoff: { lat: p.lat, lng: p.lng, address: p.name } } })}
                />
              ))}
              {favorites.map((p) => (
                <QuickChip
                  key={p.id}
                  testId={`quick-fav-${p.id}`}
                  icon={Star}
                  label={p.name}
                  starred
                  onClick={() => nav("/app/book", { state: { dropoff: { lat: p.lat, lng: p.lng, address: p.address } } })}
                />
              ))}
            </div>
          </div>

          {frequent.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">Usual routes</p>
              <div className="space-y-1.5">
                {frequent.slice(0, 2).map((f) => (
                  <button
                    key={f.address}
                    data-testid={`frequent-${f.address.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
                    onClick={() => nav("/app/book", { state: { dropoff: { lat: f.lat, lng: f.lng, address: f.address } } })}
                    className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition active:scale-[0.98] text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary flex items-center justify-center">
                      <MapPin size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{f.address}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{f.count}× this month</p>
                    </div>
                    <ArrowRight size={14} className="text-slate-300" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <MapPin size={14} className="text-primary" />
              {vehicles.length} cars nearby
            </span>
            <span className="font-mono">Abu Dhabi · UAE</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickChip({ testId, icon: Icon, label, onClick, starred }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex-shrink-0 flex items-center gap-2 px-4 h-12 rounded-full bg-white border-2 border-slate-100 hover:border-primary hover:bg-primary-50 transition-all active:scale-95"
    >
      <Icon size={16} className="text-primary" />
      <span className="font-semibold text-sm text-slate-800">{label}</span>
      {starred && <Star size={11} className="fill-amber-400 text-amber-400" />}
    </button>
  );
}

function MenuItem({ to, icon: Icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-100 text-slate-700 text-sm font-medium transition active:scale-[0.98]"
    >
      <Icon size={16} className="text-primary" /> {label}
    </Link>
  );
}
